import PropTypes from 'prop-types';

import {Box, Skeleton, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {BlogGridInlineCtaEditorialSuppressProvider} from '@amblique/king-living/app/contentful/context/blog-grid-inline-cta-editorial-suppress';

/**
 * @override
 * EditorialWrapper component
 *
 * @param {Object} props - Component props
 * @param {Contentful.EditorialWrapper} props.component - EditorialWrapper component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement|null}
 */
export const EditorialWrapper = ({component, isComplete = true, isFetching = false, className, ...props}) => {
  const {appearance, contentCollection} = component || {};

  const styles = useMultiStyleConfig('contentful/editorial-wrapper', {
    component,
    isComplete,
    isFetching,
    variant: appearance,
  });

  if (!isComplete && isFetching) {
    return (
      <Box {...styles.root} {...props}>
        <Skeleton height="200px" />
      </Box>
    );
  }

  if (!component || !contentCollection?.items) {
    return null;
  }

  const validItems = contentCollection.items.filter(
    (item) => item != null && typeof item === 'object' && item.__typename && item.sys?.id
  );

  if (validItems.length === 0) {
    return null;
  }

  const rootClassName = ['editorial-wrapper', 'component--editorial-wrapper', className]
    .filter(Boolean)
    .join(' ');

  return (
    <Box className={rootClassName} {...styles.root} {...props}>
      <BlogGridInlineCtaEditorialSuppressProvider>
        <Box className="editorial-wrapper__container" {...styles.container}>
          {validItems.map((item) => (
            <Component key={item.sys.id} component={item} isComplete={isComplete} isFetching={isFetching} />
          ))}
        </Box>
      </BlogGridInlineCtaEditorialSuppressProvider>
    </Box>
  );
};

EditorialWrapper.displayName = 'EditorialWrapper';
EditorialWrapper.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  className: PropTypes.string,
};
